import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { api, apiGet, refreshSession, setAccessToken } from './api';

export type Role = 'owner' | 'manager' | 'member';

export interface Me {
  user: { id: string; email: string; fullName: string | null };
  group: {
    id: string;
    name: string;
    plan: string;
    status: string;
    onboardingCompleted: boolean;
    memberCount: number;
  } | null;
  role: Role;
  canSeeAll: boolean;
  entitlements: string[];
  isAdmin: boolean;
}

interface AuthState {
  me: Me | null;
  loading: boolean;
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (input: { email: string; password: string; fullName?: string; groupName?: string }) => Promise<void>;
  acceptInvite: (input: { token: string; password: string; fullName?: string }) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    try {
      const data = await apiGet<Me>('/api/auth/me');
      setMe(data);
    } catch {
      setMe(null);
    }
  }, []);

  // On boot: try to restore a session from the refresh cookie, then load /me.
  useEffect(() => {
    (async () => {
      const token = await refreshSession();
      if (token) await loadMe();
      setLoading(false);
    })();
  }, [loadMe]);

  const afterAuth = async (accessToken: string) => {
    setAccessToken(accessToken);
    await loadMe();
  };

  const login: AuthState['login'] = async (email, password) => {
    const res = await api<{ accessToken: string }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    await afterAuth(res.accessToken);
  };

  const register: AuthState['register'] = async (input) => {
    const res = await api<{ accessToken: string }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    await afterAuth(res.accessToken);
  };

  const acceptInvite: AuthState['acceptInvite'] = async (input) => {
    const res = await api<{ accessToken: string }>('/api/auth/accept-invite', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    await afterAuth(res.accessToken);
  };

  const logout: AuthState['logout'] = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
    } catch {
      /* ignore */
    }
    setAccessToken(null);
    setMe(null);
  };

  return (
    <AuthContext.Provider value={{ me, loading, refresh: loadMe, login, register, acceptInvite, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
